import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ShieldCheck, CalendarClock } from 'lucide-react';
import { getCalApi } from '@calcom/embed-react';
import { useTranslation } from 'react-i18next';
import whatsappLogo from '../../assets/whatsapp_logo.webp';
import { CAL_UI_CONFIG, calLink } from '../../config';

/* ──────────────────────────────────────────────────────────────────────────
   CTA FINAL — Un solo objetivo: agendar la DEMO en vivo.
   Aquí es donde el lead se conecta por Google Meet (vía Cal.com) o por WhatsApp
   y se cierra la venta en persona. Sin cotizar ni planes: solo la demo.
     · WhatsApp (puerta primaria): el agente agenda, igual que con sus clientes.
     · Cal.com (puerta secundaria): el lead decidido elige horario → Meet + Calendar.
   ────────────────────────────────────────────────────────────────────────── */

const WA_NUMBER = '593987119183';
const WA_MSG = 'Hola, quiero agendar una demo de 15 min de RurayHUB';

const FinalCTA: React.FC = () => {
  const { t } = useTranslation();
  const waLink = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(t('finalcta.wa_msg', WA_MSG))}`;

  // Inicializa el embed de Cal.com (tema oscuro, acento de marca).
  useEffect(() => {
    (async () => {
      const cal = await getCalApi();
      cal('ui', { ...CAL_UI_CONFIG, hideEventTypeDetails: true });
    })();
  }, []);

  return (
    <section id="cta-final" style={{ padding: 'clamp(4.5rem, 9vh, 7rem) 0 clamp(5rem, 10vh, 8rem)', position: 'relative' }}>
      <div className="container">
        <motion.div
          className="cta-final-card"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          style={{
            position: 'relative', overflow: 'hidden', borderRadius: 32,
            padding: 'clamp(2.75rem, 6vw, 5.5rem) clamp(1.5rem, 5vw, 4rem)',
            textAlign: 'center',
            backdropFilter: 'blur(16px)',
          }}
        >
          {/* ── Atmósfera ── */}
          <div aria-hidden style={{ position: 'absolute', top: 0, left: '12%', right: '12%', height: 1, background: 'linear-gradient(90deg, transparent, var(--accent-teal), transparent)', opacity: 0.55, pointerEvents: 'none' }} />
          <div aria-hidden style={{ position: 'absolute', top: -90, left: '50%', transform: 'translateX(-50%)', width: 360, height: 360, borderRadius: '50%', background: '#00e5d1', opacity: 0.16, filter: 'blur(100px)', pointerEvents: 'none' }} />
          <div aria-hidden style={{ position: 'absolute', bottom: -120, right: -40, width: 320, height: 320, borderRadius: '50%', background: 'var(--accent-purple)', opacity: 0.08, filter: 'blur(110px)', pointerEvents: 'none' }} />
          <div aria-hidden style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(rgba(255,255,255,0.05) 1px, transparent 1px)', backgroundSize: '24px 24px', WebkitMaskImage: 'radial-gradient(120% 90% at 50% 0%, #000 0%, transparent 72%)', maskImage: 'radial-gradient(120% 90% at 50% 0%, #000 0%, transparent 72%)', opacity: 0.5, pointerEvents: 'none' }} />

          <div style={{ position: 'relative', zIndex: 1, maxWidth: 720, margin: '0 auto' }}>
            {/* Eyebrow: disponibilidad en vivo (ping) */}
            <motion.div
              className="cta-final-badge"
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.5 }}
              transition={{ duration: 0.5 }}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 9, padding: '0.4rem 0.9rem 0.4rem 0.75rem', borderRadius: 999, marginBottom: '1.6rem', fontFamily: 'var(--font-heading)', fontSize: '0.74rem', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' }}
            >
              <motion.span
                aria-hidden
                style={{ width: 8, height: 8, borderRadius: '50%', background: '#25D366', flexShrink: 0 }}
                animate={{ boxShadow: ['0 0 0 0 rgba(37,211,102,0.55)', '0 0 0 7px rgba(37,211,102,0)', '0 0 0 0 rgba(37,211,102,0)'] }}
                transition={{ duration: 2, repeat: Infinity, ease: 'easeOut' }}
              />
              {t('finalcta.status', 'En línea · respondemos en minutos')}
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ delay: 0.08, duration: 0.6 }}
              style={{ fontFamily: 'var(--font-heading)', fontSize: 'clamp(2.3rem, 5vw, 4.1rem)', fontWeight: 300, color: 'var(--text-primary)', margin: '0 0 1.3rem', lineHeight: 1.06, letterSpacing: '-0.035em' }}
            >
              {t('finalcta.title1', '¿Cuántos pacientes más')}<br /><span className="grad-text" style={{ fontWeight: 800 }}>{t('finalcta.title2', 'vas a perder mañana?')}</span>
            </motion.h2>

            {/* Subcopy — texto original */}
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ delay: 0.14, duration: 0.6 }}
              style={{ color: 'var(--text-secondary)', fontSize: 'clamp(1rem, 2vw, 1.18rem)', lineHeight: 1.6, margin: '0 auto 2.3rem', maxWidth: 540 }}
            >
              {t('finalcta.subtitle', 'Nuestro agente te agenda una demo de 15 min en Google Meet — la misma experiencia que vivirán tus pacientes.')}
            </motion.p>

            {/* Dos puertas a la demo */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ delay: 0.22, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 14, marginBottom: '1.7rem' }}
            >
              {/* Puerta primaria — WhatsApp (mismo botón que el Hero) */}
              <a
                href={waLink}
                target="_blank"
                rel="noopener noreferrer"
                className="trial-btn"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 10,
                  padding: '1.05rem 2rem', borderRadius: 980, fontWeight: 700,
                  fontFamily: 'var(--font-heading)', fontSize: '1.02rem',
                  textDecoration: 'none', cursor: 'pointer',
                }}
              >
                <img src={whatsappLogo} alt="" style={{ width: 20, height: 20, objectFit: 'contain' }} />
                {t('finalcta.btn_wa', 'Agendar demo por WhatsApp')}
                <ArrowRight size={18} />
              </a>

              {/* Puerta secundaria — Cal.com (elegir horario) */}
              <motion.button
                data-cal-link={calLink()}
                data-cal-config='{"theme":"dark"}'
                className="cta-final-secondary"
                whileHover={{ y: -2, borderColor: 'rgba(0,229,209,0.55)', boxShadow: '0 10px 26px -10px rgba(0,229,209,0.35)' }}
                whileTap={{ scale: 0.98 }}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 10,
                  padding: '1.05rem 1.8rem', borderRadius: 16, fontWeight: 600,
                  fontFamily: 'var(--font-heading)', fontSize: '0.98rem',
                  color: 'var(--text-primary)', cursor: 'pointer',
                  backdropFilter: 'blur(8px)',
                }}
              >
                <CalendarClock size={18} color="var(--text-primary)" />
                {t('finalcta.btn_cal', 'Prefiero elegir mi horario')}
              </motion.button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, amount: 0.5 }}
              transition={{ delay: 0.32, duration: 0.6 }}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: 'var(--text-muted)', fontSize: '0.84rem' }}
            >
              <ShieldCheck size={15} color="var(--text-primary)" />
              {t('finalcta.guarantee', 'Sin tarjeta · Sin formularios')}
            </motion.div>
          </div>
        </motion.div>
      </div>

      <style>{`
        .cta-final-card {
          background: radial-gradient(120% 140% at 50% 0%, rgba(0,229,209,0.15) 0%, rgba(139,92,246,0.11) 48%, rgba(8,8,8,0.6) 100%);
        }
        [data-theme='light'] .cta-final-card {
          background: radial-gradient(120% 140% at 50% 0%, rgba(0,229,209,0.16) 0%, rgba(139,92,246,0.1) 48%, rgba(255,255,255,0.9) 100%);
          box-shadow: 0 20px 50px -30px rgba(0,0,0,0.25);
        }

        .cta-final-badge { background: rgba(37,211,102,0.08); border: 1px solid rgba(37,211,102,0.25); color: #7ee2a8; }
        [data-theme='light'] .cta-final-badge { background: rgba(37,211,102,0.12); border: 1px solid rgba(37,211,102,0.35); color: #0d8a4f; }

        .cta-final-secondary { background: rgba(255,255,255,0.04); border: 1px solid var(--border-lighter); }
        [data-theme='light'] .cta-final-secondary { background: rgba(0,0,0,0.03); border: 1px solid rgba(0,0,0,0.12); }
      `}</style>
    </section>
  );
};

export default FinalCTA;
